import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { EditIconGeneral } from '@/app/common/icons/general'
import { parseRequestError } from '@/app/common/utilities'
import usePutSystemCatalogItem from '@/queries/system_catalog/hooks/usePutSystemCatalogItem'
import {
  SystemCatalogCategory,
  SystemCatalogItem,
} from '@/queries/system_catalog/system_catalog.schema'
import { Dialog, DialogContent, DialogTrigger } from '@/shad/ui/dialog'
import { useToast } from '@/shad/ui/toast/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { CategorySelector } from './_components/CategorySelector/CategorySelector'
import {
  removeCategory,
  toggleCategory,
} from './_components/CategorySelector/CategorySelector.utils'
import { ItemNameField } from './_components/ItemNameField'
import { EditItemFormSchema, EditItemFormValues } from './item-form.schema'

interface EditItemFormProps {
  item: SystemCatalogItem
  categories: SystemCatalogCategory[]
  onSuccess: () => void
}

function EditItemPopup({ item, categories, onSuccess }: EditItemFormProps) {
  const [isOpen, setIsOpen] = useState(false)
  const { toast } = useToast()

  // Get category IDs that include this item
  const initialCategoryIds = categories
    .filter((category) => {
      const categoryItemIds = category.items.map((item) => item.id)

      return categoryItemIds.includes(item.id)
    })
    .map((category) => category.id.toString())

  const { control, getValues, handleSubmit } = useForm<EditItemFormValues>({
    resolver: zodResolver(EditItemFormSchema),
    defaultValues: {
      categories: initialCategoryIds,
      name: item.name,
      // Note: type is not editable as per requirements
    },
  })

  const editItemMutation = usePutSystemCatalogItem({
    onSuccess: () => {
      toast({
        variant: 'default',
        title: 'Item successfully Edited',
        description: 'The item has been edited in the system catalog.',
      })
      onSuccess()
      setIsOpen(false)
    },
    onError: (error) => {
      const errorMsg = parseRequestError(error)
      toast({
        variant: 'destructive',
        title: 'Error editing item',
        description:
          errorMsg.message ||
          'There was an error editing the item. Please try again.',
      })
    },
  })

  const options = categories
    .filter((category) => category.type_name === item.type_name)
    .map((category) => {
      return {
        value: category.id.toString(),
        label: category.name,
      }
    })

  const onSubmit = handleSubmit((data) => {
    editItemMutation.mutate({
      id: item.id,
      name: data.name,
      categories: data.categories,
    })
  })

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="">
          <EditIconGeneral />
        </Button>
      </DialogTrigger>
      <DialogContent className="space-y-4">
        <h2 className="text-lg font-medium">Edit Item</h2>
        <form onSubmit={onSubmit} className="space-y-3">
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => {
              return (
                <ItemNameField
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )
            }}
          />
          <Controller
            control={control}
            name="categories"
            render={({ field, fieldState }) => {
              return (
                <CategorySelector
                  error={fieldState.error?.message}
                  removeCategory={(item) => {
                    const newCategories = removeCategory(
                      getValues().categories,
                      item
                    )
                    field.onChange(newCategories)
                  }}
                  options={options}
                  handleSelect={(item) => {
                    const newCategories = toggleCategory(
                      getValues().categories,
                      item
                    )
                    field.onChange(newCategories)
                  }}
                  selectedCategories={field.value}
                />
              )
            }}
          />
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              onClick={() => {
                setIsOpen(false)
              }}
              variant={'outline'}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                status={editItemMutation.status}
                buttonTxt={'Save Changes'}
              />
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditItemPopup
